import { Alert, Button, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { SignaturePicker, type SignaturePickerHandle } from '@/features/account';
import {
  useCreateSignatureMutation,
  useGetSignaturesQuery,
  useMarkDocumentViewedMutation,
} from '@/features/annual-review';
import { useSignDocumentMutation } from '@/features/forms';
import { toastShown } from '@/features/ui';

import { signableDocumentIdOf, titleOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { SignableDocument } from './SignableDocument';
import { signableDocumentUrlOf } from './signableDocumentUrl';
import { SignDeclaration } from './SignDeclaration';
import { signOutcome } from './signOutcome';

interface SignStepProps {
  item: OutstandingItem;
  onDone: () => void;
  onSkip: () => void;
}

/**
 * The signing step of the guided flow.
 *
 * Shares `SignaturePicker` with Settings and with the standalone sign dialog,
 * so there is exactly one signature canvas in the app. What differs here is
 * only the framing: the step advances the queue on success instead of closing,
 * and it offers "Skip for now" because a director part-way through four items
 * should be able to pass one without abandoning the other three.
 *
 * The signed document is the row's *signable* id — for an annual review that is
 * the solvency resolution, not the row itself.
 */
export const SignStep = ({ item, onDone, onSkip }: SignStepProps) => {
  const dispatch = useAppDispatch();
  const { data: signatures = [], isLoading } = useGetSignaturesQuery(undefined);
  const [signDocument] = useSignDocumentMutation();
  const [createSignature] = useCreateSignatureMutation();
  const [markDocumentViewed] = useMarkDocumentViewedMutation();

  const pickerRef = useRef<SignaturePickerHandle>(null);
  // Seeded false and driven by the picker, not by `signatures.length`: that
  // list is empty on the first render while the request is in flight, and
  // `useState` ignores every later argument — which left a director holding a
  // saved signature with a permanently disabled button.
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [saveSignature, setSaveSignature] = useState(false);
  const [hasViewedDocuments, setHasViewedDocuments] = useState(false);
  // Asked of the picker rather than assumed: the Saved tab is the default when
  // the director has one, and offering to save a saved signature would add a
  // duplicate to their list every time they signed.
  const [isNewSignature, setIsNewSignature] = useState(true);

  const submit = async () => {
    const resolved = pickerRef.current?.resolve() ?? null;
    if (resolved === null) {
      setError('That signature could not be prepared. Try drawing a simpler one.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const documentId = signableDocumentIdOf(item);

    try {
      await signDocument({ documentId, signature: resolved.image }).unwrap();
    } catch {
      setError('That document could not be signed. Please try again.');
      setIsSubmitting(false);
      return;
    }

    // Recorded after the signature lands, and neither can undo it — so neither
    // may report a failure to sign. Saving is surfaced because the director
    // asked for it; the viewed flag is bookkeeping they never see, and stopping
    // a queue of four items over it would cost them more than it is worth.
    const wantsSave = saveSignature && resolved.isNew;
    let saved = wantsSave;

    if (wantsSave) {
      try {
        await createSignature({ signature: resolved.image }).unwrap();
      } catch {
        saved = false;
      }
    }

    if (hasViewedDocuments) {
      await markDocumentViewed({ asicCompanyId: item.asicCompanyId, documentId });
    }

    const outcome = signOutcome(wantsSave, saved);
    dispatch(toastShown(outcome.message, outcome.severity));

    setIsSubmitting(false);
    onDone();
  };

  return (
    <div className="flex flex-col gap-4">
      {error !== '' && (
        <Alert severity="error" role="alert" className="rounded-lg">
          {error}
        </Alert>
      )}

      {/* Above the picker, in the order the decision is actually made: read
          the document, then sign it. A card rather than an inline frame, so
          "Sign and continue" stays in thumb reach on a phone (§4) — reading
          opens the app's shared viewer over the top. */}
      <SignableDocument href={signableDocumentUrlOf(item)} title={titleOf(item)} />

      <SignaturePicker
        ref={pickerRef}
        savedSignatures={signatures}
        isLoadingSaved={isLoading}
        onReadyChange={setIsReady}
        onIsNewChange={setIsNewSignature}
      />

      <SignDeclaration
        canSave={isNewSignature}
        saveSignature={saveSignature}
        onSaveSignatureChange={setSaveSignature}
        hasViewedDocuments={hasViewedDocuments}
        onHasViewedDocumentsChange={setHasViewedDocuments}
      />

      <Typography variant="caption" color="text.secondary">
        Applying your signature is legally binding.
      </Typography>

      {/* Primary action at the bottom, full width: this is the thumb's reach on
          a phone, and the flow is a phone-first screen (§4). */}
      <div className="flex flex-col gap-2">
        <Button
          variant="contained"
          size="large"
          onClick={() => void submit()}
          disabled={!isReady || !hasViewedDocuments || isSubmitting}
          className="w-full rounded-full"
        >
          {isSubmitting ? 'Signing…' : 'Sign and continue'}
        </Button>

        <Button
          onClick={onSkip}
          disabled={isSubmitting}
          className="w-full rounded-full"
          color="inherit"
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};
